import { useEffect, useRef } from 'react'
import { useEditorState } from '@tiptap/react'
import type { Editor } from '@tiptap/core'
import { FindBarChrome } from '@/components/features/find-bar-chrome'
import { focusAndSelectFindInput } from '@/lib/focus-find-input'
import { testIds } from '@/lib/test-ids'
import { findPluginKey } from './tiptap-find-extension'
import type { FindState } from './tiptap-find-extension'

interface TiptapFindBarProps {
  editor: Editor
  onClose: () => void
  /** Increment to re-focus the query field and select the current string. */
  focusRequestId?: number
}

export function TiptapFindBar({ editor, onClose, focusRequestId = 0 }: TiptapFindBarProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  const { query, results, current, scrollGeneration } = useEditorState({
    editor,
    selector: ({ editor: e }) => {
      const s = findPluginKey.getState(e.state) as FindState | undefined
      return s ?? { query: '', results: [], current: 0, scrollGeneration: 0 }
    },
  })

  useEffect(() => {
    const id = window.requestAnimationFrame(() => {
      focusAndSelectFindInput(inputRef.current)
    })
    return () => window.cancelAnimationFrame(id)
  }, [focusRequestId])

  // Only scroll on query / next / prev - not when paste or delete recomputes matches.
  useEffect(() => {
    if (!scrollGeneration) return
    const el = document.querySelector('.find-match--active') as HTMLElement | null
    el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [scrollGeneration])

  const handleChange = (value: string) => {
    editor.commands.setFindQuery(value)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const isMod = e.metaKey || e.ctrlKey
    if (e.key === 'Enter' || (isMod && e.key === 'g')) {
      e.preventDefault()
      if (e.shiftKey) {
        editor.commands.findPrev()
      } else {
        editor.commands.findNext()
      }
    } else if (isMod && e.key.toLowerCase() === 'f') {
      e.preventDefault()
      focusAndSelectFindInput(e.currentTarget)
    } else if (e.key === 'Escape') {
      e.preventDefault()
      handleClose()
    }
  }

  const handleClose = () => {
    editor.commands.clearFind()
    onClose()
    editor.commands.focus()
  }

  const hasQuery = query.trim().length > 0
  const noResults = hasQuery && results.length === 0
  const matchLabel = hasQuery
    ? results.length === 0
      ? 'No results'
      : `${current + 1} of ${results.length}`
    : ''

  // sticky + h-0 overlay: stays pinned to the scrollport while content scrolls
  // (absolute inside the tall editor root scrolls away with the document).
  return (
    <div className="sticky top-0 z-20 h-0 overflow-visible flex justify-end pr-3 pt-2 pointer-events-none">
      <div className="pointer-events-auto">
        <FindBarChrome
          inputRef={inputRef}
          query={query}
          matchLabel={matchLabel}
          noResults={noResults}
          prevDisabled={!hasQuery || results.length === 0}
          nextDisabled={!hasQuery || results.length === 0}
          onQueryChange={handleChange}
          onKeyDown={handleKeyDown}
          onPrev={() => editor.commands.findPrev()}
          onNext={() => editor.commands.findNext()}
          onClose={handleClose}
          prevTitle="Previous match (Shift+Enter or Cmd+Shift+G)"
          nextTitle="Next match (Enter or Cmd+G)"
          testIds={testIds.tiptapFind}
        />
      </div>
    </div>
  )
}
